import axios, { InternalAxiosRequestConfig, Method } from 'axios'
import { API_URL, MASTER_KEY } from 'config'
import { getAltchaToken } from 'managers/altcha-manager'
import { store } from 'store/index'
import { settingsSelectors } from 'store/settings'
import { ApiService } from '@runelab/odin-common'

axios.defaults.baseURL = API_URL
axios.defaults.headers.common['Content-Type'] = 'application/json'

const extendParams = () => {
  const state = store.getState()
  const tenantId = settingsSelectors.tenantId(state)

  return { tenantId: tenantId }
}

// qlik: the Qlik user the extension sent over postMessage (`mimirInputs`), on every request
// so the server can forward it to the Qlik API. Host-supplied: not proof of identity.
const addQlikUserHeaders = (config: InternalAxiosRequestConfig) => {
  const { qlikUserDirectory, qlikUserId } = settingsSelectors.agentInputs(store.getState())

  if (typeof qlikUserDirectory === 'string' && typeof qlikUserId === 'string') {
    config.headers['x-qlik-user-directory'] = qlikUserDirectory
    config.headers['x-qlik-user-id'] = qlikUserId
  }
}

const addTenantToRequest = (config: InternalAxiosRequestConfig, methods: Method[]) => {
  const state = store.getState()
  const tenantId = settingsSelectors.tenantId(state)

  addQlikUserHeaders(config)

  const tenantParams = { tenantId: tenantId }

  if (config.method?.toLowerCase() && methods.includes(config.method.toLowerCase() as Method)) {
    if (config.data instanceof FormData) {
      if (tenantId) {
        config.data.append('tenantId', tenantId)
      }
    } else {
      config.data = { ...(config.data || {}), ...tenantParams }
    }
  } else {
    config.params = { ...config.params, ...tenantParams }
  }

  return config
}

axios.interceptors.request.use(async (config) => {
  const state = store.getState()
  const token = settingsSelectors.token(state) || MASTER_KEY

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  config.params = { ...config.params, ...extendParams() }

  return addTenantToRequest(config, ['post', 'put'])
})

export const masterAxios = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

masterAxios.interceptors.request.use(
  async (config) => {
    config.headers['Content-Type'] = 'application/json'
    config.headers.Authorization = `Bearer ${MASTER_KEY}`

    config.params = { ...config.params, ...extendParams() }

    return addTenantToRequest(config, ['post', 'put'])
  },
  (err) => Promise.resolve(err),
)

export const captchaAxios = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

captchaAxios.interceptors.request.use(
  async (config) => {
    const altchaToken = await getAltchaToken(masterAxios)
    const state = store.getState()
    const token = settingsSelectors.token(state)
    config.headers['x-altcha-token'] = altchaToken

    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    config.params = { ...config.params, ...extendParams() }

    return addTenantToRequest(config, ['post', 'put'])
  },
  (err) => Promise.resolve(err),
)

export const ssoAxios = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
})

ssoAxios.interceptors.request.use(
  async (config) => {
    config.params = { ...config.params, ...extendParams() }

    return addTenantToRequest(config, ['post', 'put'])
  },
  (err) => Promise.resolve(err),
)

export const mainApiService = new ApiService(axios)
export const masterApiService = new ApiService(masterAxios)
